import { ActivityStatus } from '@showbie-socrative/socrative-utils';
import { QueryClient } from '@tanstack/react-query';
import { ActivityTimersResult } from '~api/activities/activityTimersApi';
import { decamelizeKeys } from 'xcase';

import { studentResponseCacheUpdater } from '~teacher/components/ActivityResult/hooks/useActivityLiveEvent/pubnubMessage/cacheUpdaters/studentResponseCacheUpdater';
import { activityStatusCacheUpdater } from '~teacher/hook';

import { getLongMessage } from '../../../../../../api/longMessage/longMessageApi';
import { studentFinishedCacheUpdater } from './cacheUpdaters/studentFinishedCacheUpdater';
import { studentNameSetCacheUpdater } from './cacheUpdaters/studentNameSetCacheUpdater';
import { PubNubMessageKey, PubnubMessage } from './types';

// snake case keys from messages are mapped to camelCase local versions.

/**
 * Debounces timer query invalidation so that a burst of PubNub events
 * (e.g. 200 students starting timers near-simultaneously) collapses
 * into a single API refetch after the events settle.
 *
 * Two delays are used:
 *   TIMER_INVALIDATION_DELAY_MS (2 s)  — bulk events (StudentTimerStarted) where
 *                                         all students act within milliseconds of
 *                                         each other at the start of a quiz.
 *   STUDENT_FINISHED_DELAY_MS  (300 ms) — StudentFinished events. Much shorter so
 *                                         the teacher sees the correct frozen
 *                                         remaining_seconds quickly, while still
 *                                         coalescing a burst (e.g. 100 students
 *                                         finishing at the same time) into a single
 *                                         refetch instead of 100 requests.
 */
const TIMER_INVALIDATION_DELAY_MS = 2000;
const STUDENT_FINISHED_DELAY_MS = 300;
const pendingTimerInvalidations = new Map<
  number,
  ReturnType<typeof setTimeout>
>();

function invalidateTimersDebounced(
  queryClient: QueryClient,
  activityInstanceId: number,
  delayMs = TIMER_INVALIDATION_DELAY_MS
) {
  const existing = pendingTimerInvalidations.get(activityInstanceId);
  if (existing) {
    clearTimeout(existing);
  }

  pendingTimerInvalidations.set(
    activityInstanceId,
    setTimeout(() => {
      pendingTimerInvalidations.delete(activityInstanceId);
      queryClient.invalidateQueries(['activity', 'timers', activityInstanceId]);
    }, delayMs)
  );
}

export const handleMessageEvent = ({
  queryClient,
  message,
  isRosteredRoom,
  activityInstanceId,
}: {
  queryClient: QueryClient;
  message: PubnubMessage;
  isRosteredRoom: boolean;
  activityInstanceId: number;
}) => {
  if (__DEV__) {
    console.debug(`isRostered: ${isRosteredRoom}`);
    console.debug(message);
  }

  switch (message.key) {
    case PubNubMessageKey.StudentFinished: {
      const {
        data: { user_uuid },
      } = message;

      studentFinishedCacheUpdater({
        queryClient: queryClient,
        activityInstanceId: activityInstanceId,
        userUuid: user_uuid,
      });

      // Optimistically mark this student's timer as finished in the cache so
      // the teacher sees the correct icon immediately, without waiting for the
      // 300 ms debounced refetch. Without this, deriveLiveTimerState briefly
      // sees stale timer data (state:'running' with remaining>0) while
      // finishedStudentUuids already has the student — causing a flash of
      // "Quiz Submitted N seconds" before transitioning to "Time Expired".
      //
      // For running timers we compute optimistic remaining from expiresAt so
      // that early submissions still correctly show "Quiz Submitted N seconds"
      // while timers that ran to zero show "Time Expired" immediately.
      queryClient.setQueryData(
        ['activity', 'timers', activityInstanceId],
        (oldData: ActivityTimersResult | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              timers: oldData.data.timers.map((t) => {
                if (t.userUuid !== user_uuid) return t;
                let optimisticRemaining = t.remainingSeconds ?? 0;
                if (t.state === 'running' && t.expiresAt) {
                  const remainingMs =
                    new Date(t.expiresAt).getTime() - Date.now();
                  optimisticRemaining = Math.max(
                    0,
                    Math.floor(remainingMs / 1000)
                  );
                }
                return {
                  ...t,
                  state: 'finished' as const,
                  remainingSeconds: optimisticRemaining,
                  expiresAt: null,
                };
              }),
            },
          };
        }
      );

      // Short debounce (300 ms) so the teacher sees the correct remaining time
      // quickly without firing one request per student when many finish at once.
      invalidateTimersDebounced(
        queryClient,
        activityInstanceId,
        STUDENT_FINISHED_DELAY_MS
      );

      break;
    }
    case PubNubMessageKey.StudentResponse: {
      const { data } = message;

      studentResponseCacheUpdater({
        queryClient: queryClient,
        activityInstanceId: activityInstanceId,
        rawResponse: data,
      });

      break;
    }
    case PubNubMessageKey.StudentStartedQuiz: {
      invalidateTimersDebounced(queryClient, activityInstanceId);

      break;
    }
    case PubNubMessageKey.StudentTimerStarted: {
      invalidateTimersDebounced(queryClient, activityInstanceId);

      break;
    }
    case PubNubMessageKey.StudentNameSet: {
      const {
        data: { student_name, user_uuid, user_previous_uuid },
      } = message;

      studentNameSetCacheUpdater({
        queryClient: queryClient,
        activityInstanceId: activityInstanceId,
        userUuid: user_uuid,
        userPreviousUuid: user_previous_uuid,
        studentName: student_name,
        isRosteredRoom: isRosteredRoom,
      });
      break;
    }
    case PubNubMessageKey.LongMessage: {
      const {
        data: { longMessageId },
      } = message;

      getLongMessage(longMessageId, activityInstanceId)
        .then((results) => {
          if (results?.data?.message?.data) {
            studentResponseCacheUpdater({
              queryClient: queryClient,
              activityInstanceId: activityInstanceId,
              rawResponse: decamelizeKeys(results.data.message.data),
            });
          }
        })
        .catch((error) => {
          console.error(error);
        });
      break;
    }
    case PubNubMessageKey.ActivityChange: {
      const { data } = message;

      switch (data) {
        case 'activity_paused': {
          activityStatusCacheUpdater({
            queryClient: queryClient,
            activityStatus: ActivityStatus.Paused,
            activityInstanceId: activityInstanceId,
          });

          queryClient.invalidateQueries([
            'activity',
            'timers',
            activityInstanceId,
          ]);
          break;
        }
        case 'activity_resumed': {
          activityStatusCacheUpdater({
            queryClient: queryClient,
            activityStatus: ActivityStatus.Running,
            activityInstanceId: activityInstanceId,
          });

          queryClient.invalidateQueries([
            'activity',
            'timers',
            activityInstanceId,
          ]);
          break;
        }
        case 'activity_ended': {
          activityStatusCacheUpdater({
            queryClient: queryClient,
            activityStatus: ActivityStatus.Finished,
            activityInstanceId: activityInstanceId,
          });

          queryClient.invalidateQueries([
            'activity',
            'timers',
            activityInstanceId,
          ]);
          break;
        }
        default: {
          if (__DEV__) {
            console.debug('Unhandled ActivityChange message', message);
          }
          break;
        }
      }
      break;
    }
    default: {
      if (__DEV__) {
        console.debug('Unhandled Pubnub message', message);
      }
      break;
    }
  }
};
